/**
 * Permission System for Palmera
 * Defines roles, permissions, and access control utilities
 */

// Role hierarchy (higher number = more permissions)
export const ROLE_LEVELS = {
  owner: 100,
  admin: 80,
  manager: 50,
  employee: 10,
};

// All available permissions
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',
  
  // Employees/Staff
  EMPLOYEES_VIEW: 'employees.view',
  EMPLOYEES_CREATE: 'employees.create',
  EMPLOYEES_EDIT: 'employees.edit',
  EMPLOYEES_DELETE: 'employees.delete',
  
  // Schedule/Shifts
  SHIFTS_VIEW: 'shifts.view',
  SHIFTS_VIEW_OWN: 'shifts.view.own',
  SHIFTS_CREATE: 'shifts.create',
  SHIFTS_EDIT: 'shifts.edit',
  SHIFTS_DELETE: 'shifts.delete',
  
  // Salaries/Payments
  SALARIES_VIEW: 'salaries.view',
  SALARIES_VIEW_OWN: 'salaries.view.own',
  SALARIES_CREATE: 'salaries.create',
  SALARIES_EDIT: 'salaries.edit',
  
  // Expenses
  EXPENSES_VIEW: 'expenses.view',
  EXPENSES_CREATE: 'expenses.create',
  EXPENSES_EDIT: 'expenses.edit',
  EXPENSES_DELETE: 'expenses.delete',
  
  // Revenue
  REVENUE_VIEW: 'revenue.view',
  REVENUE_CREATE: 'revenue.create',
  REVENUE_EDIT: 'revenue.edit',
  REVENUE_DELETE: 'revenue.delete',
  
  // Financial Overview
  FINANCIAL_VIEW: 'financial.view',
  
  // Settings
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_EDIT: 'settings.edit',
  SETTINGS_MYDATA: 'settings.mydata',
  
  // Organization Management
  ORG_MEMBERS_VIEW: 'org.members.view',
  ORG_MEMBERS_INVITE: 'org.members.invite',
  ORG_MEMBERS_EDIT: 'org.members.edit',
  ORG_MEMBERS_REMOVE: 'org.members.remove',
  ORG_SETTINGS_EDIT: 'org.settings.edit',
  ORG_DELETE: 'org.delete',
};

// Role-based permission mapping
export const ROLE_PERMISSIONS = {
  owner: [
    // Owner has ALL permissions
    '*',
  ],
  admin: [
    PERMISSIONS.DASHBOARD_VIEW,
    // Employees
    PERMISSIONS.EMPLOYEES_VIEW,
    PERMISSIONS.EMPLOYEES_CREATE,
    PERMISSIONS.EMPLOYEES_EDIT,
    PERMISSIONS.EMPLOYEES_DELETE,
    // Shifts
    PERMISSIONS.SHIFTS_VIEW,
    PERMISSIONS.SHIFTS_CREATE,
    PERMISSIONS.SHIFTS_EDIT,
    PERMISSIONS.SHIFTS_DELETE,
    // Salaries
    PERMISSIONS.SALARIES_VIEW,
    PERMISSIONS.SALARIES_CREATE,
    PERMISSIONS.SALARIES_EDIT,
    // Expenses
    PERMISSIONS.EXPENSES_VIEW,
    PERMISSIONS.EXPENSES_CREATE,
    PERMISSIONS.EXPENSES_EDIT,
    PERMISSIONS.EXPENSES_DELETE,
    // Revenue
    PERMISSIONS.REVENUE_VIEW,
    PERMISSIONS.REVENUE_CREATE,
    PERMISSIONS.REVENUE_EDIT,
    PERMISSIONS.REVENUE_DELETE,
    // Financial
    PERMISSIONS.FINANCIAL_VIEW,
    // Settings
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.SETTINGS_EDIT,
    PERMISSIONS.SETTINGS_MYDATA,
    // Organization (limited)
    PERMISSIONS.ORG_MEMBERS_VIEW,
    PERMISSIONS.ORG_MEMBERS_INVITE,
    PERMISSIONS.ORG_MEMBERS_EDIT,
    PERMISSIONS.ORG_SETTINGS_EDIT,
    // Note: Admin cannot delete org or remove owner
  ],
  manager: [
    PERMISSIONS.DASHBOARD_VIEW,
    // Employees (view only)
    PERMISSIONS.EMPLOYEES_VIEW,
    // Shifts (full access)
    PERMISSIONS.SHIFTS_VIEW,
    PERMISSIONS.SHIFTS_CREATE,
    PERMISSIONS.SHIFTS_EDIT,
    PERMISSIONS.SHIFTS_DELETE,
    // Salaries (view only)
    PERMISSIONS.SALARIES_VIEW,
    // Expenses (view only)
    PERMISSIONS.EXPENSES_VIEW,
    // Revenue (full access)
    PERMISSIONS.REVENUE_VIEW,
    PERMISSIONS.REVENUE_CREATE,
    PERMISSIONS.REVENUE_EDIT,
    // Financial (view)
    PERMISSIONS.FINANCIAL_VIEW,
    // Organization (view members only)
    PERMISSIONS.ORG_MEMBERS_VIEW,
  ],
  employee: [
    PERMISSIONS.DASHBOARD_VIEW,
    // Own shifts only
    PERMISSIONS.SHIFTS_VIEW_OWN,
    // Own salaries only
    PERMISSIONS.SALARIES_VIEW_OWN,
  ],
};

/**
 * Check if a role has a specific permission
 */
export function roleHasPermission(role, permission) {
  if (!role || !permission) return false;
  
  const permissions = ROLE_PERMISSIONS[role] || [];
  
  // Wildcard means all permissions
  if (permissions.includes('*')) return true;
  
  // Direct permission check
  if (permissions.includes(permission)) return true;
  
  // Check for parent permission (e.g., 'employees' includes 'employees.view')
  const parts = permission.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const parentPerm = parts.slice(0, i).join('.');
    if (permissions.includes(parentPerm)) return true;
  }
  
  return false;
}

/**
 * Check if user has at least the required role level
 */
export function hasRoleLevel(userRole, requiredRole) {
  const userLevel = ROLE_LEVELS[userRole] || 0;
  const requiredLevel = ROLE_LEVELS[requiredRole] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Get human-readable role name in Greek
 */
export function getRoleName(role) {
  const names = {
    owner: 'Ιδιοκτήτης',
    admin: 'Διαχειριστής',
    manager: 'Manager',
    employee: 'Υπάλληλος',
  };
  return names[role] || role;
}

/**
 * Get role color for UI
 */
export function getRoleColor(role) {
  const colors = {
    owner: '#7c3aed', // Purple
    admin: '#2563eb', // Blue
    manager: '#059669', // Green
    employee: '#6b7280', // Gray
  };
  return colors[role] || '#6b7280';
}

/**
 * Get all roles available for assignment
 * Owner can assign any role, Admin can assign manager/employee
 */
export function getAssignableRoles(currentUserRole) {
  if (currentUserRole === 'owner') {
    return ['admin', 'manager', 'employee'];
  }
  if (currentUserRole === 'admin') {
    return ['manager', 'employee'];
  }
  return [];
}

/**
 * Permission groups for UI display
 */
export const PERMISSION_GROUPS = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    nameGr: 'Επισκόπηση',
    permissions: [PERMISSIONS.DASHBOARD_VIEW],
  },
  {
    id: 'employees',
    name: 'Employees',
    nameGr: 'Προσωπικό',
    permissions: [
      PERMISSIONS.EMPLOYEES_VIEW,
      PERMISSIONS.EMPLOYEES_CREATE,
      PERMISSIONS.EMPLOYEES_EDIT,
      PERMISSIONS.EMPLOYEES_DELETE,
    ],
  },
  {
    id: 'shifts',
    name: 'Schedule',
    nameGr: 'Πρόγραμμα',
    permissions: [
      PERMISSIONS.SHIFTS_VIEW,
      PERMISSIONS.SHIFTS_VIEW_OWN,
      PERMISSIONS.SHIFTS_CREATE,
      PERMISSIONS.SHIFTS_EDIT,
      PERMISSIONS.SHIFTS_DELETE,
    ],
  },
  {
    id: 'salaries',
    name: 'Payments',
    nameGr: 'Πληρωμές',
    permissions: [
      PERMISSIONS.SALARIES_VIEW,
      PERMISSIONS.SALARIES_VIEW_OWN,
      PERMISSIONS.SALARIES_CREATE,
      PERMISSIONS.SALARIES_EDIT,
    ],
  },
  {
    id: 'expenses',
    name: 'Expenses',
    nameGr: 'Έξοδα',
    permissions: [
      PERMISSIONS.EXPENSES_VIEW,
      PERMISSIONS.EXPENSES_CREATE,
      PERMISSIONS.EXPENSES_EDIT,
      PERMISSIONS.EXPENSES_DELETE,
    ],
  },
  {
    id: 'revenue',
    name: 'Revenue',
    nameGr: 'Έσοδα',
    permissions: [
      PERMISSIONS.REVENUE_VIEW,
      PERMISSIONS.REVENUE_CREATE,
      PERMISSIONS.REVENUE_EDIT,
      PERMISSIONS.REVENUE_DELETE,
    ],
  },
  {
    id: 'financial',
    name: 'Financial',
    nameGr: 'Οικονομικά',
    permissions: [PERMISSIONS.FINANCIAL_VIEW],
  },
  {
    id: 'settings',
    name: 'Settings',
    nameGr: 'Ρυθμίσεις',
    permissions: [
      PERMISSIONS.SETTINGS_VIEW,
      PERMISSIONS.SETTINGS_EDIT,
      PERMISSIONS.SETTINGS_MYDATA,
    ],
  },
  {
    id: 'organization',
    name: 'Organization',
    nameGr: 'Οργανισμός',
    permissions: [
      PERMISSIONS.ORG_MEMBERS_VIEW,
      PERMISSIONS.ORG_MEMBERS_INVITE,
      PERMISSIONS.ORG_MEMBERS_EDIT,
      PERMISSIONS.ORG_MEMBERS_REMOVE,
      PERMISSIONS.ORG_SETTINGS_EDIT,
      PERMISSIONS.ORG_DELETE,
    ],
  },
];

/**
 * Get permission description in Greek
 */
export function getPermissionDescription(permission) {
  const descriptions = {
    [PERMISSIONS.DASHBOARD_VIEW]: 'Προβολή επισκόπησης',
    [PERMISSIONS.EMPLOYEES_VIEW]: 'Προβολή προσωπικού',
    [PERMISSIONS.EMPLOYEES_CREATE]: 'Δημιουργία υπαλλήλων',
    [PERMISSIONS.EMPLOYEES_EDIT]: 'Επεξεργασία υπαλλήλων',
    [PERMISSIONS.EMPLOYEES_DELETE]: 'Διαγραφή υπαλλήλων',
    [PERMISSIONS.SHIFTS_VIEW]: 'Προβολή προγράμματος',
    [PERMISSIONS.SHIFTS_VIEW_OWN]: 'Προβολή μόνο δικών βαρδιών',
    [PERMISSIONS.SHIFTS_CREATE]: 'Δημιουργία βαρδιών',
    [PERMISSIONS.SHIFTS_EDIT]: 'Επεξεργασία βαρδιών',
    [PERMISSIONS.SHIFTS_DELETE]: 'Διαγραφή βαρδιών',
    [PERMISSIONS.SALARIES_VIEW]: 'Προβολή πληρωμών',
    [PERMISSIONS.SALARIES_VIEW_OWN]: 'Προβολή μόνο δικών πληρωμών',
    [PERMISSIONS.SALARIES_CREATE]: 'Καταχώρηση πληρωμών',
    [PERMISSIONS.SALARIES_EDIT]: 'Επεξεργασία πληρωμών',
    [PERMISSIONS.EXPENSES_VIEW]: 'Προβολή εξόδων',
    [PERMISSIONS.EXPENSES_CREATE]: 'Καταχώρηση εξόδων',
    [PERMISSIONS.EXPENSES_EDIT]: 'Επεξεργασία εξόδων',
    [PERMISSIONS.EXPENSES_DELETE]: 'Διαγραφή εξόδων',
    [PERMISSIONS.REVENUE_VIEW]: 'Προβολή εσόδων',
    [PERMISSIONS.REVENUE_CREATE]: 'Καταχώρηση εσόδων',
    [PERMISSIONS.REVENUE_EDIT]: 'Επεξεργασία εσόδων',
    [PERMISSIONS.REVENUE_DELETE]: 'Διαγραφή εσόδων',
    [PERMISSIONS.FINANCIAL_VIEW]: 'Προβολή οικονομικής εικόνας',
    [PERMISSIONS.SETTINGS_VIEW]: 'Προβολή ρυθμίσεων',
    [PERMISSIONS.SETTINGS_EDIT]: 'Επεξεργασία ρυθμίσεων',
    [PERMISSIONS.SETTINGS_MYDATA]: 'Ρυθμίσεις myDATA',
    [PERMISSIONS.ORG_MEMBERS_VIEW]: 'Προβολή μελών',
    [PERMISSIONS.ORG_MEMBERS_INVITE]: 'Πρόσκληση μελών',
    [PERMISSIONS.ORG_MEMBERS_EDIT]: 'Αλλαγή ρόλων μελών',
    [PERMISSIONS.ORG_MEMBERS_REMOVE]: 'Αφαίρεση μελών',
    [PERMISSIONS.ORG_SETTINGS_EDIT]: 'Ρυθμίσεις οργανισμού',
    [PERMISSIONS.ORG_DELETE]: 'Διαγραφή οργανισμού',
  };
  return descriptions[permission] || permission;
}

